import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { ArrowUpRight } from "lucide-react";
import { Layout } from "@/components/Layout";
import { CaseDetail } from "@/components/cases/CaseDetail";
import { casePostQueryOptions } from "@/lib/public-content.queries";
import { metaDescription, pageTitle } from "@/lib/seo-text";

// Muss exportiert sein: Die automatisch ausgelagerte Seitenkomponente greift
// aus ihrem Lazy-Chunk darauf zu. Eine nur lokale Konstante führt beim Laden
// des gesamten Route-Trees zu einem fehlenden Named Export.
export const SITE_ORIGIN = "https://creategy.de";

export const Route = createFileRoute("/cases/$slug")({
  // Gleiche Regel wie im Blog: fehlender Case = echter 404, keine leere Seite.
  loader: async ({ context, params }) => {
    const post = await context.queryClient.ensureQueryData(
      casePostQueryOptions(params.slug),
    );
    if (!post) throw notFound();
    return post;
  },

  head: ({ loaderData, params }) => {
    const post = loaderData;
    const title = post ? pageTitle(post.title, " – Case | creategy") : "Case – creategy";
    const description = metaDescription(
      post?.excerpt ??
        "Case Study von creategy: Wie wir politische Kommunikation und Kampagnen für unsere Kundinnen und Kunden erfolgreich umgesetzt haben.",
    );
    const url = `${SITE_ORIGIN}/cases/${params.slug}`;
    const meta: Array<Record<string, string>> = [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: url },
    ];
    if (post?.cover_url) {
      meta.push({ property: "og:image", content: post.cover_url });
      meta.push({ name: "twitter:image", content: post.cover_url });
    }
    return { meta, links: [{ rel: "canonical", href: url }] };
  },
  notFoundComponent: () => (
    <Layout>
      <div className="mx-auto max-w-3xl px-6 md:px-12 section-y-hero text-center">
        <h1 className="heading-h1">Case nicht gefunden.</h1>
        <Link to="/" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold underline transition-opacity hover:opacity-80">
          Zur Startseite
          <ArrowUpRight className="h-4 w-4" aria-hidden />
        </Link>
      </div>
    </Layout>
  ),
  errorComponent: () => (
    <Layout>
      <div className="mx-auto max-w-3xl px-6 md:px-12 section-y-hero text-center">
        <h1 className="heading-h1">Case konnte nicht geladen werden.</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Bitte versuchen Sie es später erneut.
        </p>
      </div>
    </Layout>
  ),
  component: CasePage,
});

function CasePage() {
  const { slug } = Route.useParams();
  const { data: post } = useSuspenseQuery(casePostQueryOptions(slug));
  if (!post) throw notFound();
  return <CaseDetail post={post} shareUrl={`${SITE_ORIGIN}/cases/${post.slug}`} />;
}
